import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useGetQuoteModuleSettings } from '@views/screens/Quote/hooks';
import { Col, Row } from 'antd';
import { routeFileSelector } from '@utilz/i18n.js';

import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';
import { useCollaboratorsValidation } from '@/views/screens/Shipment/components/QuoteCard/hooks/useCollaboratorsValidation';

import { ReactComponent as DocsListIcon } from '../../../../../assets/icons/docs-list.svg';
import { ReactComponent as FilesIcon } from '../../../../../assets/icons/files-records.svg';
import { ReactComponent as NotesIcon } from '../../../../../assets/icons/notes.svg';
import { ReactComponent as SearchIcon } from '../../../../../assets/icons/search-icon.svg';
import { ReactComponent as ShipmentIcon } from '../../../../../assets/icons/shipment-file.svg';
import { ReactComponent as TruckIcon } from '../../../../../assets/icons/truck.svg';
import SvgConverter from '../../../../components/Common/SvgConverter';
import CollapsiblePanel from '../../../../components/Tabs/CollapsiblePanel';
import { TAB_KEYS } from '../../constants';
import QuoteBiddingLogs from '../Tabs/BiddingLogs/QuoteBiddingLogs';
import QuoteCollaboratorLogs from '../Tabs/CollboratorLogs/QuoteCollaboratorLogs';
import HallDocumentDetails from './Tabs/DocumentDetails/HallDocumentDetails';
import HallNotesDetails from './Tabs/NotesDetails/HallNotesDetails';
import HallProductDetails from './Tabs/ProductDetails/HallProductDetails';
import HallQuoteDetailsForm from './Tabs/QuoteDetails/hall-quote-details-form.jsx';
import RateShopResultsTab from './Tabs/RateShop/RateShopResultsTab';
import ShippingTermsSearchForm from './Tabs/ShippingTerms/ShippingTermsSearchForm';
import HallTransportationDetails from './Tabs/TransportationDetails/HallTransportationDetails';

const GenerateTitle = ({ title, Icon, size = 25 }) => {
  return (
    <Row gutter={10} align="middle">
      <Col className="lh-0">
        <SvgConverter width={size} height={size} Icon={Icon} />
      </Col>
      <Col className="lh-0">
        <label className="lh-0 text-dark cursor-pointer">{title}</label>
      </Col>
    </Row>
  );
};

const HallCreateGetQuoteTabs = ({ activeKey, setActiveKey }) => {
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);
  const { copyQuoteKey } = useSelector((state) => state.common);

  const { data: quoteModuleSettings } = useGetQuoteModuleSettings();
  const isRatingEnabled = quoteModuleSettings?.isRatingEnabled;
  const { isCollaboratorsEnabled } = useCollaboratorsValidation();

  const handleTabClick = (keys) => {
    setActiveKey(keys);
  };

  useEffect(() => {
    if (copyQuoteKey) {
      showNotification({
        message: 'Quote was successfully copied',
        type: NOTIFICATION_TYPES.SUCCESS,
      });
    }
  }, [copyQuoteKey]);

  return (
    <CollapsiblePanel
      forceRender
      handleTabClick={handleTabClick}
      activeKey={activeKey}
      tabs={[
        {
          content: <HallQuoteDetailsForm />,
          header: <GenerateTitle title={t('translation:getQuote.titles.quoteDetails')} Icon={ShipmentIcon} size={25} />,
          tabKey: TAB_KEYS.quoteDetails,
        },
        {
          content: <HallTransportationDetails />,
          header: (
            <GenerateTitle title={t('translation:getQuote.titles.transportationDetails')} Icon={TruckIcon} size={25} />
          ),
          tabKey: TAB_KEYS.transportationDetails,
        },
        {
          content: <HallProductDetails />,
          header: (
            <GenerateTitle title={t('translation:default.fields.productDetails')} Icon={DocsListIcon} size={25} />
          ),
          tabKey: TAB_KEYS.productDetails,
        },
        {
          content: <ShippingTermsSearchForm />,
          header: <GenerateTitle title="Shipping Terms" Icon={SearchIcon} size={25} />,
          tabKey: TAB_KEYS.shippingTerms,
        },
        {
          content: <HallNotesDetails />,
          header: <GenerateTitle title={t('translation:getQuote.titles.quoteNotes')} Icon={NotesIcon} size={25} />,
          tabKey: TAB_KEYS.notes,
        },
        {
          content: <HallDocumentDetails />,
          header: <GenerateTitle title={t('translation:default.documentManager.title')} Icon={FilesIcon} size={25} />,
          tabKey: TAB_KEYS.documents,
        },
        ...(isCollaboratorsEnabled
          ? [
              {
                content: <QuoteCollaboratorLogs />,
                header: <GenerateTitle title="Collaborators" Icon={NotesIcon} size={25} />,
                hidePadding: true,
                tabKey: TAB_KEYS.collaborators,
              },
            ]
          : []),
        {
          content: <QuoteBiddingLogs />,
          header: <GenerateTitle title="Bidding" Icon={NotesIcon} size={25} />,
          hidePadding: true,
          tabKey: TAB_KEYS.bidding,
        },
        ...(isRatingEnabled
          ? [
              {
                content: <RateShopResultsTab />,
                header: <GenerateTitle title="Rate Shop Results" Icon={TruckIcon} />,
                hidePadding: true,
                tabKey: TAB_KEYS.rate,
              },
            ]
          : []),
      ]}
    />
  );
};

export default HallCreateGetQuoteTabs;
